import React from 'react';
// @ts-ignore — resolved at build time via Vite aliases
import { useContentToken, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import type { ComponentContentSchema } from '@vibesites/runtime';

interface CitySEOContentProps {
  cityName: string;
  stateAbbr: string;
  stateName: string;
  renderHeading?: boolean;
}

function isHtml(str: string) {
  return /<[a-z][\s\S]*>/i.test(str);
}

export function CitySEOContent({ cityName, stateAbbr, stateName, renderHeading = true }: CitySEOContentProps) {
  const body = useContentToken('citymarket.seo_body', {
    defaultValue: '',
  });
  const ctaText = useContentToken('citymarket.seo_cta_text', {
    defaultValue: `Find Your Perfect Apartment in ${cityName}, ${stateAbbr}, From Case & Associates`,
  });
  const ctaBody = useContentToken('citymarket.seo_cta_body', {
    defaultValue: '',
  });

  const stateSlug = stateAbbr.toLowerCase();

  const defaultBody = (
    <>
      <p>
        Explore apartments for rent in {cityName}, {stateName}. Case &amp; Associates offers beautiful,
        reasonably priced apartment homes with modern amenities. Browse our communities above and
        schedule a tour today!
      </p>
      <p>
        Case &amp; Associates proudly offers beautiful, reasonably priced apartments, whether you're
        looking for a 1 bedroom, 2 bedroom or 3 bedroom home! Our communities are conveniently located
        near major roadways, making it simple to get on the road and explore everything {cityName} has to offer.
      </p>
    </>
  );

  const defaultCtaBody = (
    <>
      <p>
        The search for the perfect apartment ends with Case &amp; Associates! Our modern and stylish
        pet-friendly apartment communities have plenty of floor plans to choose from and amenities to
        enhance your apartment's ambience.
      </p>
      <p>
        Explore our <a href={`/apartments/${stateSlug}/`}><strong>apartments in the {cityName} area</strong></a> online
        and schedule a tour today! If you have any questions, feel free to{' '}
        <a href="tel:9184921983"><strong>call us</strong></a> or{' '}
        <a href="/contact-us/"><strong>contact us online</strong></a>. We're excited to show you around!
      </p>
    </>
  );

  const content = (
    <>
      <EditableToken path="citymarket.seo_body" type="richtext" label="SEO Body" as="div">
        {body && isHtml(body) ? (
          <div dangerouslySetInnerHTML={{ __html: body }} />
        ) : body ? (
          <p>{body}</p>
        ) : (
          defaultBody
        )}
      </EditableToken>

      <EditableToken path="citymarket.seo_cta_text" type="text" label="SEO CTA Heading" as="h2">
        {ctaText}
      </EditableToken>

      <EditableToken path="citymarket.seo_cta_body" type="richtext" label="SEO CTA Body" as="div">
        {ctaBody && isHtml(ctaBody) ? (
          <div dangerouslySetInnerHTML={{ __html: ctaBody }} />
        ) : ctaBody ? (
          <p>{ctaBody}</p>
        ) : (
          defaultCtaBody
        )}
      </EditableToken>
    </>
  );

  if (!renderHeading) return content;

  return (
    <div className="case-seo-content">
      <RichTextContent path="citymarket.seo_heading" label="SEO Heading" as="h2" defaultValue={'Apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>'} />
      {content}
    </div>
  );
}

export default CitySEOContent;
